import { Fragment, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { GovReportSummary, getReports, governanceErrorMessage } from "../../../utils/icGovernance";
import { useViewport } from "../../../utils/useViewport";
import { useGovernance } from "./GovernanceLayout";
import {
  CARD_BORDER,
  OverallStatusChip,
  PRIMARY,
  ReportStatusChip,
  clientLabel,
  dateOnly,
  reportUrl,
  vendorLabel,
} from "./governanceStatus";
import { INK, MONO, MUTED, PlainText, localDay } from "./registers/cells";

/**
 * Weekly Project Status — the vendor's published weekly reports (drafts never
 * reach Thrive). The admin acknowledges each one on its own page.
 */

const AMBER = "#92400e";

const awaiting = (r: GovReportSummary) => r.status === "PUBLISHED" && r.acknowledgementRequired;

const WeekLink = ({ report }: { report: GovReportSummary }) => (
  <Link
    to={reportUrl(report.id)}
    onClick={(e) => e.stopPropagation()}
    style={{ fontFamily: MONO, fontWeight: 700, color: PRIMARY, textDecoration: "none", whiteSpace: "nowrap" }}
  >
    {dateOnly(report.weekEnding)}
  </Link>
);

const AckBy = ({ report }: { report: GovReportSummary }) =>
  awaiting(report) ? (
    <Box component="span" sx={{ color: AMBER, fontWeight: 600 }}>
      Awaiting acknowledgement
    </Box>
  ) : (
    <PlainText value={report.acknowledgement?.by} />
  );

const ReportCard = ({ report }: { report: GovReportSummary }) => (
  <Box
    component={Link}
    to={reportUrl(report.id)}
    sx={{
      display: "block",
      padding: "12px 16px",
      borderBottom: CARD_BORDER,
      color: "inherit",
      textDecoration: "none",
      "&:active": { backgroundColor: "#f5f5f5" },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
      <Typography sx={{ fontFamily: MONO, fontWeight: 700, color: PRIMARY }}>
        Week ending {dateOnly(report.weekEnding)}
      </Typography>
      <OverallStatusChip status={report.overallStatus} />
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ mt: 0.75, flexWrap: "wrap" }}>
      <ReportStatusChip status={report.status} />
      <Typography variant="body2" sx={{ color: MUTED }}>
        {report.counts?.completed ?? 0} completed · {report.counts?.planned ?? 0} planned ·{" "}
        {report.counts?.decisions ?? 0} decisions · {report.counts?.risks ?? 0} risks
      </Typography>
    </Stack>
    {awaiting(report) ? (
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        <AckBy report={report} />
      </Typography>
    ) : report.acknowledgement?.by ? (
      <Typography variant="body2" sx={{ mt: 0.5, color: MUTED }}>
        Acknowledged by {report.acknowledgement.by} · {localDay(report.acknowledgement.at)}
      </Typography>
    ) : null}
  </Box>
);

const WeeklyStatusList = () => {
  const { settings } = useGovernance();
  const navigate = useNavigate();
  const { isPhone } = useViewport();
  const [rows, setRows] = useState<GovReportSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getReports()
      .then((page) => {
        if (alive) setRows(page.items ?? []);
      })
      .catch((err) => {
        if (!alive) return;
        console.error("Failed to load weekly reports:", err);
        setError(governanceErrorMessage(err));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const waiting = useMemo(() => rows.filter(awaiting), [rows]);
  const client = clientLabel(settings);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mb: 2 }}>
        <Typography sx={{ fontWeight: 700, color: INK }}>
          {loading ? "Weekly reports" : `${rows.length} weekly ${rows.length === 1 ? "report" : "reports"}`}
        </Typography>
        <Typography variant="body2" sx={{ color: MUTED }}>
          {vendorLabel(settings)} publishes; {client} acknowledges. Newest week first.
        </Typography>
      </Stack>

      {!loading && waiting.length > 0 ? (
        <Box
          role="status"
          sx={{
            mb: 2,
            px: 1.75,
            py: 1.25,
            borderRadius: "8px",
            backgroundColor: "#fef3c7",
            border: "1px solid #f59e0b",
            color: AMBER,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {waiting.length} weekly {waiting.length === 1 ? "report awaits" : "reports await"} your acknowledgement:{" "}
          {waiting.map((r, i) => (
            <Fragment key={r.id}>
              {i > 0 ? ", " : null}
              <Link to={reportUrl(r.id)} style={{ fontFamily: MONO, color: AMBER }}>
                {dateOnly(r.weekEnding)}
              </Link>
            </Fragment>
          ))}
        </Box>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", textAlign: "center", py: 6 }}>
          <CircularProgress sx={{ color: PRIMARY }} />
        </Paper>
      ) : rows.length === 0 ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px" }}>
          <Typography sx={{ textAlign: "center", color: MUTED, py: 5, px: 2 }}>
            {error ? "No weekly reports to show." : "No weekly reports have been published yet."}
          </Typography>
        </Paper>
      ) : isPhone ? (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", overflow: "hidden" }}>
          {rows.map((r) => (
            <ReportCard key={r.id} report={r} />
          ))}
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", width: 0, minWidth: "100%", overflowX: "auto" }}>
          <Table size="small" aria-label="Weekly reports">
            <TableHead>
              <TableRow
                sx={{
                  "& th": { fontWeight: 700, fontSize: 12, color: "#3f4753", whiteSpace: "nowrap", backgroundColor: "#f8f9fb" },
                }}
              >
                <TableCell>Week Ending</TableCell>
                <TableCell>Overall Status</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Completed</TableCell>
                <TableCell align="right">Planned</TableCell>
                <TableCell align="right">Decisions</TableCell>
                <TableCell align="right">Risks</TableCell>
                <TableCell>{client} Acknowledged By</TableCell>
                <TableCell>Acknowledgement Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{
                    cursor: "pointer",
                    "& td": { whiteSpace: "nowrap", color: INK },
                    ...(awaiting(r) ? { backgroundColor: "#fffbeb" } : {}),
                  }}
                  onClick={() => navigate(reportUrl(r.id))}
                >
                  <TableCell>
                    <WeekLink report={r} />
                  </TableCell>
                  <TableCell>
                    <OverallStatusChip status={r.overallStatus} />
                  </TableCell>
                  <TableCell>
                    <ReportStatusChip status={r.status} />
                  </TableCell>
                  <TableCell align="right">{r.counts?.completed ?? 0}</TableCell>
                  <TableCell align="right">{r.counts?.planned ?? 0}</TableCell>
                  <TableCell align="right">{r.counts?.decisions ?? 0}</TableCell>
                  <TableCell align="right">{r.counts?.risks ?? 0}</TableCell>
                  <TableCell>
                    <AckBy report={r} />
                  </TableCell>
                  <TableCell>{localDay(r.acknowledgement?.at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
};

export default WeeklyStatusList;
